// invest_FE/src/pages/account/AuthCallbackPage.js
import React, { useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { useBanner } from '../../context/BannerContext';
import { useLanguage } from '../../hooks/useLanguage';
import { apiFetch } from '../../utils/apiClient'; // Import Role enum
import { BASE_URL } from '../../utils/config';
import AgeConfirmationModal from '../../components/verification/AgeConfirmationModal';
//import { Role } from '../../utils/enums';
import './AuthCallbackPage.css';

const AuthCallbackPage = () => {
  const { t } = useTranslation();
  const { user, login, logoutUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { addBanner } = useBanner();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useLanguage();
  const [isAgeModalOpen, setIsAgeModalOpen] = useState(false);

  useEffect(() => {
    const processAuth = async () => {
      const params = new URLSearchParams(location.search);
      const accessToken = params.get('accessToken');
      const refreshToken = params.get('refreshToken');
      const needsAgeConfirmation = params.get('needsAgeConfirmation') === 'true';

      if (accessToken && refreshToken) {
        try {
          // Fetch the full user object using the new access token.
          // This endpoint returns the same user object structure as a normal login.
          const response = await apiFetch(`${BASE_URL}/authUser/login/success`, { method: 'GET' }, accessToken);
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || t('authCallbackPage.fetchUserDetailsError'));
          }
          const data = await response.json();
          // --- DIAGNOSTIC LOG ---
          console.log('Data received from /login/success:', data);
          console.log('User object to be set in context:', { ...data.user, token: accessToken });
          // --- END DIAGNOSTIC LOG ---

          // Use the centralized login function from the context
          const loginData = { accessToken, refreshToken, user: data.user };
          login(loginData);

          // Show verification banner if needed
          if (!data.user.isEmailVerified) {
            const userEmail = data.user.email;
            const handleResendVerification = async () => {
              try {
                const res = await fetch(`${BASE_URL}/authUser/request-verification-email`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: userEmail }),
                });
                const resData = await res.json();
                if (res.ok) {
                  addNotification(t('authCallbackPage.resendEmailSuccess', { email: userEmail }), 'success', 5000);
                } else {
                  addNotification(t('authCallbackPage.resendEmailError', { error: resData.message || t('authCallbackPage.genericErrorText') }), 'error', 5000);
                }
              } catch (err) {
                addNotification(t('authCallbackPage.resendEmailNetworkError'), 'error', 5000);
              }
            };
            addBanner(t('authCallbackPage.verifyEmailBanner', { email: userEmail }), 'warning', [
              { text: t('authCallbackPage.resendEmailButton'), onClick: handleResendVerification },
              { text: t('authCallbackPage.goToAccountButton'), onClick: () => navigate(`/${lang}/preferences`) },
            ]);
          } else if (!data.user.isPhoneVerified) {
            addBanner(t('authCallbackPage.verifyPhoneBanner'), 'warning', [{ text: t('authCallbackPage.goToMyAccountButton'), onClick: () => navigate(`/${lang}/preferences`) }]);
          } else {
            // Only show success notification if no other banner is shown
            addNotification(t('authCallbackPage.loginSuccess'), 'success', 2000);
          }

          if (needsAgeConfirmation) {
            setIsAgeModalOpen(true);
          } else {
            navigate(`/${lang}`);
          }
        } catch (error) {
          addNotification(t('authCallbackPage.loginError', { message: error.message }), 'error');
          navigate(`/${lang}/login`);
        }
      } else {
        addNotification(t('authCallbackPage.invalidCallbackError'), 'error');
        navigate(`/${lang}/login`);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, addNotification, addBanner, navigate, t, lang, login]); // Add login to dependencies

  const handleConfirmAge = async () => {
    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/confirm-age`,
        {
          method: 'POST',
        },
        user.token
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('authCallbackPage.ageConfirmError'));
      }

      const data = await response.json();
      // Re-login with the updated user object from the backend to ensure context is fully synced.
      // We need to pass the existing tokens along with the new user data.
      const loginData = { accessToken: user.token, refreshToken: localStorage.getItem('refreshToken'), user: data.user };
      login(loginData);
      setIsAgeModalOpen(false);
      addNotification(t('authCallbackPage.ageConfirmSuccess'), 'success');
      navigate(`/${lang}`);
    } catch (error) {
      addNotification(t('authCallbackPage.ageConfirmGenericError', { message: error.message }), 'error', 0, true);
      handleCancelAge(); // Log out the user on failure as per the strict policy
    }
  };

  const handleCancelAge = () => {
    setIsAgeModalOpen(false);
    addNotification(t('authCallbackPage.ageCancelMessage'), 'error', 0, true);
    logoutUser(); // Use the centralized logout function
    navigate(`/${lang}/login`);
  };

  return (
    <>
      <div className="auth-callback-container">
        <h2>{t('authCallbackPage.processingTitle')}</h2>
        <p>{t('authCallbackPage.processingMessage')}</p>
        {/* Optional: Add a spinner here */}
      </div>
      <AgeConfirmationModal isOpen={isAgeModalOpen} onConfirm={handleConfirmAge} onCancel={handleCancelAge} />
    </>
  );
};

export default AuthCallbackPage;
